import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useCreateDocument,
  useRetrieveCurrentPlanzone,
  useRetrieveDocumentTemplatesFolder,
} from "@planzone/view-models";
import { Identifiable, Document, document, Folder } from "@planzone/dto";

import { Button } from "@common/button";
import { Modal } from "@common/modal";
import { Form } from "./form";
import { getEmptyDocuments } from "./utils";
import { Props } from "./types";
import styles from "./styles.scss";

function extractTemplateFolderContent(
  templateFolder: Identifiable<Folder>,
): Identifiable<Document>[] {
  return templateFolder.children.filter(
    (child) => child.type === "document",
  ) as Identifiable<Document>[];
}

function DocumentCreation({
  documentType,
  type,
  namespace,
  projectName,
  cancel,
}: Props) {
  const { t } = useTranslation();
  const [documentName, setDocumentName] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState<
    Identifiable<Document>
  >({ ...document, uuid: "" });
  const [selectedFolder, setSelectedFolder] =
    useState<Identifiable<Folder> | null>(null);
  const { createDocument, isRequestPending } = useCreateDocument();
  const [templates, setTemplates] = React.useState([...getEmptyDocuments()]);
  const { planzone } = useRetrieveCurrentPlanzone();
  const {
    retrieveDocumentTemplatesFolder,
    isRequestSuccess,
    isRequestPending: isRetrieveDocumentTemplatesFolderPending,
    folder,
  } = useRetrieveDocumentTemplatesFolder();

  React.useEffect(() => {
    retrieveDocumentTemplatesFolder({ planzone: planzone.uuid });
  }, []);

  React.useEffect(() => {
    isRequestSuccess &&
      setTemplates([
        ...getEmptyDocuments(),
        ...extractTemplateFolderContent(folder),
      ]);
  }, [isRequestSuccess]);

  return (
    <Modal header={t("documentCreation.title")} close={cancel}>
      <form
        className={styles.main}
        onSubmit={(e) => {
          e.preventDefault();
          createDocument({
            name: `${documentName}${documentType}`,
            template: selectedTemplate.uuid,
            folder: selectedFolder ?? { ...folder, uuid: "" },
            namespace,
            type,
          });
        }}
      >
        <Form
          documentType={documentType}
          isRequestPending={isRetrieveDocumentTemplatesFolderPending}
          selectedFolder={selectedFolder}
          templates={templates}
          namespace={namespace}
          type={type}
          projectName={projectName}
          setSelectedFolder={setSelectedFolder}
          setDocumentName={setDocumentName}
          selectedTemplate={selectedTemplate}
          setSelectedTemplate={setSelectedTemplate}
        />
        <div className={styles.buttons}>
          <Button type="cancel" press={cancel}>
            {t("documentCreation.cancel")}
          </Button>
          <Button
            type="submit"
            status={isRequestPending ? "pending" : "none"}
            isDisabled={
              isRequestPending ||
              !selectedFolder ||
              !selectedTemplate.uuid ||
              !documentName
            }
          >
            {t("documentCreation.submit")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export { DocumentCreation };
